import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";

export type OpportunityRecord = {
  id: string;
  customer_id: string;
  vehicle_id: string | null;
  public_vehicle_intake_id?: string | null;
  public_financing_request_id?: string | null;
  origin_contact_id: string | null;
  assigned_to: string;
  source: string;
  stage: string;
  status: string;
  priority: string;
  last_interaction_at: string | null;
  next_action_at: string | null;
  notes: string | null;
  closed_reason: string | null;
  outcome_code: string | null;
  reopen_reason: string | null;
  created_at: string;
};
export type PublicVehicleIntake = {
  id: string;
  public_reference: string;
  request_type: string;
  registration: string;
  vin: string | null;
  make: string;
  model: string;
  version: string | null;
  first_registration_year: number | null;
  mileage_km: number;
  vehicle_condition: string;
  desired_value_cents: number | null;
  currency_code: string;
  notes: string | null;
  photo_paths: string[];
  submitted_at: string;
};
export type PublicFinancingRequest = {id:string;public_reference:string;vehicle_description:string|null;vehicle_price_cents:number;down_payment_cents:number;financed_amount_cents:number;term_months:number;estimated_installment_cents:number|null;nominal_annual_rate:number|null;annual_percentage_rate:number|null;submitted_at:string};
export type OpportunityTask = {
  id: string;
  opportunity_id: string | null;
  contact_id: string | null;
  conversation_id: string | null;
  customer_id: string | null;
  vehicle_id: string | null;
  title: string;
  description: string | null;
  due_at: string;
  priority_starts_at: string;
  priority: string;
  status: string;
  assigned_to: string;
  completion_note: string | null;
  withdrawal_reason: string | null;
  review_status: string | null;
  review_note: string | null;
};
export type OpportunityVehicle = {
  id: string;
  opportunity_id: string;
  vehicle_id: string;
  relationship_type: string;
  response: string;
  notes: string | null;
  created_at: string;
};
export type OpportunityEvent = {
  id: number;
  entity_type: string;
  entity_id: string;
  event_type: string;
  created_at: string;
};
export type OpportunityVehicleOption = {
  id: string;
  registration: string | null;
  stock_number: string | null;
  make: string;
  model: string;
  stock_status: string;
};
export type OpportunityTeamMember = {
  user_id: string;
  display_name: string;
  role_key: string;
  status: string;
};

const priorityLabels: Record<string, string> = {
  low: "Baixa",
  normal: "Normal",
  high: "Alta",
  urgent: "Urgente",
};
const stageLabels: Record<string, string> = {
  new: "Novo interesse",
  contacted: "Contactado",
  qualified: "Qualificado",
  proposal: "Proposta",
  reserved: "Reservado",
};
const statusLabels: Record<string, string> = {
  active: "Em acompanhamento",
  won: "Convertida em venda",
  lost: "Perdida",
};
const outcomeLabels: Record<string, string> = {
  sale: "Venda",
  vehicle_unavailable: "Viatura indisponível",
  no_interest: "Sem interesse",
  no_response: "Sem resposta",
  financing_refused: "Financiamento recusado",
  bought_elsewhere: "Comprou noutra empresa",
  postponed: "Decisão adiada",
  duplicate: "Registo duplicado",
  other: "Outro motivo",
};
const responseLabels: Record<string, string> = {
  offered: "Apresentada",
  interested: "Demonstrou interesse",
  visit_scheduled: "Visita marcada",
  test_drive: "Test-drive",
  proposal: "Proposta enviada",
  rejected: "Recusada",
};
const sourceLabels: Record<string, string> = {
  manual: "Registo manual",
  phone: "Telefone",
  in_person: "Presencial",
  web_chat: "Chat do site",
  whatsapp: "WhatsApp",
  email: "Email",
  form: "Formulário",
  paid_ad: "Anúncio pago",
  vehicle_portal: "Portal automóvel",
  other: "Outro",
};
const localInput = (date = new Date()) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString("pt-PT") : "—";
const requestLabels: Record<string, string> = {
  sell: "Venda ao stand",
  appraise: "Avaliação",
  announce: "Anúncio",
};
const conditionLabels: Record<string, string> = {
  excellent: "Excelente",
  good: "Bom",
  fair: "Razoável",
  needs_work: "Precisa de intervenção",
};
function PublicVehicleIntakePanel({ intake }: { intake: PublicVehicleIntake }) {
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    void supabase.storage
      .from("public-vehicle-intakes")
      .createSignedUrls(intake.photo_paths, 900)
      .then(({ data }) =>
        setUrls(
          (data ?? [])
            .map((item) => item.signedUrl)
            .filter((url): url is string => Boolean(url)),
        ),
      );
  }, [intake.id]);
  return (
    <section className="public-intake-detail">
      <header>
        <div>
          <p className="eyebrow">VIATURA DO CLIENTE</p>
          <h3>
            {requestLabels[intake.request_type] || intake.request_type} ·{" "}
            {intake.public_reference}
          </h3>
        </div>
        <small>{formatDate(intake.submitted_at)}</small>
      </header>
      <div className="crm-detail-grid">
        <article>
          <small>Matrícula</small>
          <strong>{intake.registration}</strong>
        </article>
        <article>
          <small>Viatura</small>
          <strong>
            {intake.make} {intake.model}
          </strong>
          <span>
            {[intake.version, intake.first_registration_year]
              .filter(Boolean)
              .join(" · ") || "—"}
          </span>
        </article>
        <article>
          <small>Quilometragem</small>
          <strong>
            {new Intl.NumberFormat("pt-PT").format(intake.mileage_km)} km
          </strong>
        </article>
        <article>
          <small>Estado declarado</small>
          <strong>{conditionLabels[intake.vehicle_condition]}</strong>
        </article>
        <article>
          <small>Valor pretendido</small>
          <strong>
            {intake.desired_value_cents == null
              ? "Não indicado"
              : new Intl.NumberFormat("pt-PT", {
                  style: "currency",
                  currency: intake.currency_code,
                }).format(intake.desired_value_cents / 100)}
          </strong>
        </article>
        {intake.vin && (
          <article>
            <small>VIN / chassis</small>
            <strong>{intake.vin}</strong>
          </article>
        )}
        {intake.notes && (
          <article className="wide">
            <small>Informação enviada</small>
            <span>{intake.notes}</span>
          </article>
        )}
      </div>
      <div className="public-intake-photos">
        {urls.map((url, index) => (
          <a href={url} target="_blank" rel="noreferrer" key={url}>
            <img
              src={url}
              alt={`Fotografia ${index + 1} de ${intake.make} ${intake.model}`}
            />
          </a>
        ))}
      </div>
    </section>
  );
}
function PublicFinancingRequestPanel({request}:{request:PublicFinancingRequest}){
  const money=(value:number|null)=>value==null?"Não calculada":new Intl.NumberFormat("pt-PT",{style:"currency",currency:"EUR"}).format(value/100);
  return <section className="public-intake-detail financing-request-detail"><header><div><p className="eyebrow">PEDIDO DE FINANCIAMENTO</p><h3>{request.public_reference}</h3></div><small>{formatDate(request.submitted_at)}</small></header><div className="crm-detail-grid"><article><small>Viatura pretendida</small><strong>{request.vehicle_description||"Não indicada"}</strong></article><article><small>Valor da viatura</small><strong>{money(request.vehicle_price_cents)}</strong></article><article><small>Entrada inicial</small><strong>{money(request.down_payment_cents)}</strong></article><article><small>Montante pretendido</small><strong>{money(request.financed_amount_cents)}</strong></article><article><small>Prazo</small><strong>{request.term_months} meses</strong></article><article><small>Prestação indicativa</small><strong>{money(request.estimated_installment_cents)}</strong><span>{request.nominal_annual_rate==null?"Simulador não utilizado":`TAN ${request.nominal_annual_rate}% · TAEG ${request.annual_percentage_rate}%`}</span></article></div><p className="field-help">Pedido comercial recebido pelo site. Não representa aprovação de crédito nem lançamento financeiro.</p></section>;
}

type Action =
  | "edit"
  | "stage"
  | "task"
  | "vehicle"
  | "transfer"
  | "lost"
  | "reopen"
  | null;
type TaskAction = {
  task: OpportunityTask;
  kind: "complete" | "withdraw" | "review";
} | null;

export function OpportunityDialog({
  opportunity,
  publicIntake = null,
  financingRequest = null,
  customerName,
  customerEmail = null,
  customerPhone = null,
  sourceLabel = null,
  vehicles,
  team,
  tasks,
  writable,
  canSupervise,
  onConvertToSale,
  onClose,
  onChanged,
  notify,
}: {
  opportunity: OpportunityRecord;
  publicIntake?: PublicVehicleIntake | null;
  financingRequest?: PublicFinancingRequest | null;
  customerName: string;
  customerEmail?: string | null;
  customerPhone?: string | null;
  sourceLabel?: string | null;
  vehicles: OpportunityVehicleOption[];
  team: OpportunityTeamMember[];
  tasks: OpportunityTask[];
  writable: boolean;
  canSupervise: boolean;
  onConvertToSale?: (seed: {
    opportunityId: string;
    customerId: string;
    vehicleId: string;
    customerName: string;
    customerEmail: string | null;
    customerPhone: string | null;
  }) => void;
  onClose: () => void;
  onChanged: () => Promise<void>;
  notify: (text: string, kind?: "success" | "error" | "progress") => void;
}) {
  const [suggestions, setSuggestions] = useState<OpportunityVehicle[]>([]),
    [action, setAction] = useState<Action>(null),
    [taskAction, setTaskAction] = useState<TaskAction>(null),
    [busy, setBusy] = useState(false);
  const vehicleMap = useMemo(
    () => Object.fromEntries(vehicles.map((vehicle) => [vehicle.id, vehicle])),
    [vehicles],
  );
  const teamMap = useMemo(
    () =>
      Object.fromEntries(
        team.map((member) => [member.user_id, member.display_name]),
      ),
    [team],
  );
  const opportunityTasks = tasks.filter(
    (task) => task.opportunity_id === opportunity.id,
  );
  const loadSuggestions = async () => {
    const { data, error } = await supabase
      .from("stand_opportunity_vehicles")
      .select(
        "id,opportunity_id,vehicle_id,relationship_type,response,notes,created_at",
      )
      .eq("opportunity_id", opportunity.id)
      .order("created_at");
    if (error) notify(error.message, "error");
    else setSuggestions((data ?? []) as OpportunityVehicle[]);
  };
  useEffect(() => {
    void loadSuggestions();
  }, [opportunity.id]);
  const run = async (
    label: string,
    operation: () => PromiseLike<{ error: { message: string } | null }>,
    success: string,
  ) => {
    setBusy(true);
    notify(label, "progress");
    const { error } = await operation();
    setBusy(false);
    if (error) return notify(error.message, "error");
    setAction(null);
    setTaskAction(null);
    notify(success);
    await onChanged();
    await loadSuggestions();
  };
  const submitStage = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    void run(
      "A guardar a etapa…",
      () =>
        supabase
          .from("stand_opportunities")
          .update({
            stage: String(f.get("stage")),
            last_interaction_at: new Date().toISOString(),
          })
          .eq("id", opportunity.id),
      "Etapa atualizada no histórico.",
    );
  };
  const submitEdit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget),
      task = opportunityTasks.find((item) => item.status === "open");
    if (!task)
      return notify(
        "Esta oportunidade não possui uma tarefa aberta para editar.",
        "error",
      );
    void run(
      "A guardar a oportunidade…",
      () =>
        supabase.rpc("update_stand_opportunity_details", {
          p_opportunity_id: opportunity.id,
          p_display_name: String(f.get("display_name") || ""),
          p_email: String(f.get("email") || "") || null,
          p_phone: String(f.get("phone") || "") || null,
          p_source: String(f.get("source")),
          p_source_label: String(f.get("source_label") || "") || null,
          p_vehicle_id: String(f.get("vehicle_id")),
          p_assigned_to: String(f.get("assigned_to")),
          p_priority: String(f.get("priority")),
          p_notes: String(f.get("notes") || "") || null,
          p_task_id: task.id,
          p_task_title: String(f.get("task_title")),
          p_task_due_at: new Date(String(f.get("task_due_at"))).toISOString(),
          p_task_priority: String(f.get("task_priority")),
        }),
      "Oportunidade e tarefa atualizadas.",
    );
  };
  const submitTask = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    void run(
      "A criar a tarefa…",
      () =>
        supabase.rpc("create_opportunity_task", {
          p_opportunity_id: opportunity.id,
          p_title: String(f.get("title")),
          p_description: String(f.get("description") || "") || null,
          p_due_at: new Date(String(f.get("due_at"))).toISOString(),
          p_priority: String(f.get("priority")),
        }),
      "Tarefa criada e próxima ação atualizada.",
    );
  };
  const submitVehicle = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    void run(
      "A guardar a viatura apresentada…",
      () =>
        supabase.rpc("add_opportunity_vehicle", {
          p_opportunity_id: opportunity.id,
          p_vehicle_id: String(f.get("vehicle_id")),
          p_response: String(f.get("response")),
          p_notes: String(f.get("notes") || "") || null,
        }),
      "Viatura guardada no histórico da oportunidade.",
    );
  };
  const submitTransfer = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    void run(
      "A transferir a oportunidade…",
      () =>
        supabase.rpc("transfer_stand_opportunity", {
          p_opportunity_id: opportunity.id,
          p_assigned_to: String(f.get("assigned_to")),
          p_reason: String(f.get("reason")),
        }),
      "Oportunidade, contacto e tarefas transferidos.",
    );
  };
  const submitLost = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    void run(
      "A encerrar a oportunidade…",
      () =>
        supabase.rpc("close_stand_opportunity_with_outcome", {
          p_opportunity_id: opportunity.id,
          p_outcome_code: String(f.get("outcome_code")),
          p_reason: String(f.get("reason")),
        }),
      "Oportunidade encerrada como perdida, com motivo e histórico.",
    );
  };
  const submitReopen = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    void run(
      "A reabrir a oportunidade…",
      () =>
        supabase.rpc("reopen_stand_opportunity", {
          p_opportunity_id: opportunity.id,
          p_reason: String(f.get("reason")),
          p_task_title: String(f.get("task_title")),
          p_task_due_at: new Date(String(f.get("task_due_at"))).toISOString(),
          p_priority: String(f.get("priority")),
        }),
      "Oportunidade reaberta com uma nova tarefa.",
    );
  };
  const submitComplete = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!taskAction) return;
    const f = new FormData(event.currentTarget);
    void run(
      "A concluir e encadear a próxima ação…",
      () =>
        supabase.rpc("complete_opportunity_task", {
          p_task_id: taskAction.task.id,
          p_result: String(f.get("result")),
          p_next_title: String(f.get("next_title")),
          p_next_description: String(f.get("next_description") || "") || null,
          p_next_due_at: new Date(String(f.get("next_due_at"))).toISOString(),
          p_next_priority: String(f.get("next_priority")),
        }),
      "Tarefa concluída e próxima ação criada.",
    );
  };
  const submitWithdraw = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!taskAction) return;
    const f = new FormData(event.currentTarget);
    void run(
      "A registar a desistência…",
      () =>
        supabase.rpc("withdraw_opportunity_task", {
          p_task_id: taskAction.task.id,
          p_reason: String(f.get("reason")),
        }),
      "Desistência enviada para revisão e preservada no histórico.",
    );
  };
  const submitReview = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!taskAction) return;
    const f = new FormData(event.currentTarget);
    void run(
      "A guardar a revisão…",
      () =>
        supabase.rpc("review_opportunity_task", {
          p_task_id: taskAction.task.id,
          p_action: String(f.get("decision")),
          p_note: String(f.get("note")),
        }),
      "Revisão da tarefa guardada.",
    );
  };
  const mainVehicle = opportunity.vehicle_id
    ? vehicleMap[opportunity.vehicle_id]
    : undefined;
  const firstOpenTask = opportunityTasks.find((task) => task.status === "open");
  return (
    <div className="crm-backdrop">
      <section
        className={`crm-dialog opportunity-dialog priority-${opportunity.priority}`}
      >
        <header>
          <div>
            <p className="eyebrow">Oportunidade</p>
            <h2>{customerName}</h2>
            <p>
              {mainVehicle
                ? `${mainVehicle.make} ${mainVehicle.model} · ${mainVehicle.registration || mainVehicle.stock_number}`
                : publicIntake
                  ? `${publicIntake.make} ${publicIntake.model} · ${publicIntake.registration}`
                  : financingRequest
                    ? `Pedido de financiamento · ${financingRequest.public_reference}`
                    : "Sem viatura associada"}
            </p>
          </div>
          <button onClick={onClose} disabled={busy}>
            ×
          </button>
        </header>
        {publicIntake && <PublicVehicleIntakePanel intake={publicIntake} />}
        {financingRequest && <PublicFinancingRequestPanel request={financingRequest} />}
        <div className="crm-detail-grid">
          <article>
            <small>Comercial</small>
            <strong>{teamMap[opportunity.assigned_to] || "—"}</strong>
          </article>
          <article>
            <small>Estado</small>
            <strong>{statusLabels[opportunity.status]}</strong>
          </article>
          <article>
            <small>Etapa</small>
            <strong>{stageLabels[opportunity.stage]}</strong>
          </article>
          <article
            className={`priority-summary priority-${opportunity.priority}`}
          >
            <small>Prioridade</small>
            <strong>{priorityLabels[opportunity.priority]}</strong>
          </article>
          <article>
            <small>Origem</small>
            <strong>
              {sourceLabels[opportunity.source] || opportunity.source}
            </strong>
            <span>{sourceLabel || "Sem detalhe adicional"}</span>
          </article>
          <article
            className={
              !opportunity.next_action_at && opportunity.status === "active"
                ? "attention"
                : ""
            }
          >
            <small>Próxima tarefa</small>
            <strong>
              {firstOpenTask ? firstOpenTask.title : "Sem tarefa ativa"}
            </strong>
            <span>
              {firstOpenTask ? formatDate(firstOpenTask.due_at) : "—"}
            </span>
          </article>
          {opportunity.notes && (
            <article className="wide">
              <small>Notas internas</small>
              <span>{opportunity.notes}</span>
            </article>
          )}
          {opportunity.closed_reason && (
            <article className="wide closed-reason">
              <small>
                {outcomeLabels[opportunity.outcome_code || ""] || "Resultado"}
              </small>
              <strong>{opportunity.closed_reason}</strong>
            </article>
          )}
        </div>
        {writable && (
          <div className="opportunity-actions">
            {opportunity.status === "active" ? (
              <>
                {onConvertToSale && opportunity.vehicle_id && (
                  <button
                    className="crm-primary"
                    onClick={() =>
                      onConvertToSale({
                        opportunityId: opportunity.id,
                        customerId: opportunity.customer_id,
                        vehicleId: opportunity.vehicle_id!,
                        customerName,
                        customerEmail,
                        customerPhone,
                      })
                    }
                  >
                    Converter em venda
                  </button>
                )}
                <button onClick={() => setAction("edit")}>
                  Editar oportunidade
                </button>
                <button onClick={() => setAction("stage")}>
                  Alterar etapa
                </button>
                <button onClick={() => setAction("task")}>Nova tarefa</button>
                <button onClick={() => setAction("vehicle")}>
                  Apresentar viatura
                </button>
                {canSupervise && (
                  <button onClick={() => setAction("transfer")}>
                    Transferir
                  </button>
                )}
                <button className="danger" onClick={() => setAction("lost")}>
                  Encerrar como perdida
                </button>
              </>
            ) : opportunity.status === "lost" ? (
              <button
                className="crm-primary"
                onClick={() => setAction("reopen")}
              >
                Reabrir oportunidade
              </button>
            ) : null}
          </div>
        )}
        {action === "edit" && firstOpenTask && (
          <form className="crm-subform" onSubmit={submitEdit}>
            <h3>Editar oportunidade</h3>
            <div className="crm-form-grid">
              <label>
                <span>Nome do interessado</span>
                <input name="display_name" defaultValue={customerName} />
              </label>
              <label>
                <span>Origem *</span>
                <select
                  name="source"
                  required
                  defaultValue={opportunity.source}
                >
                  {Object.entries(sourceLabels).map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Telefone</span>
                <input name="phone" defaultValue={customerPhone || ""} />
              </label>
              <label>
                <span>Email</span>
                <input
                  name="email"
                  type="email"
                  defaultValue={customerEmail || ""}
                />
              </label>
              <label className="wide">
                <span>Detalhe da origem</span>
                <input name="source_label" defaultValue={sourceLabel || ""} />
              </label>
              <label>
                <span>Viatura *</span>
                <select
                  name="vehicle_id"
                  required
                  defaultValue={opportunity.vehicle_id ?? ""}
                >
                  {vehicles.map((v) => (
                    <option value={v.id} key={v.id}>
                      {v.registration || v.stock_number} · {v.make} {v.model}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Responsável *</span>
                <select
                  name="assigned_to"
                  required
                  defaultValue={opportunity.assigned_to}
                >
                  {team.map((m) => (
                    <option value={m.user_id} key={m.user_id}>
                      {m.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Prioridade da oportunidade *</span>
                <select name="priority" defaultValue={opportunity.priority}>
                  {Object.entries(priorityLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Prioridade da tarefa *</span>
                <select
                  name="task_priority"
                  defaultValue={firstOpenTask.priority}
                >
                  {Object.entries(priorityLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Primeira tarefa *</span>
                <input
                  name="task_title"
                  required
                  defaultValue={firstOpenTask.title}
                />
              </label>
              <label>
                <span>Data e hora da tarefa *</span>
                <input
                  name="task_due_at"
                  type="datetime-local"
                  required
                  defaultValue={localInput(new Date(firstOpenTask.due_at))}
                />
              </label>
              <label className="wide">
                <span>Notas internas</span>
                <textarea
                  name="notes"
                  rows={3}
                  defaultValue={opportunity.notes || ""}
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setAction(null)}>
                Cancelar
              </button>
              <button className="crm-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        {action === "stage" && (
          <form className="crm-subform" onSubmit={submitStage}>
            <h3>Alterar etapa</h3>
            <div className="crm-form-grid">
              <label className="wide">
                <span>Nova etapa *</span>
                <select name="stage" defaultValue={opportunity.stage}>
                  {Object.entries(stageLabels).map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setAction(null)}>
                Cancelar
              </button>
              <button className="crm-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        {action === "task" && (
          <form className="crm-subform" onSubmit={submitTask}>
            <h3>Nova tarefa</h3>
            <div className="crm-form-grid">
              <label className="wide">
                <span>Tarefa *</span>
                <input name="title" required />
              </label>
              <label>
                <span>Data e hora *</span>
                <input
                  name="due_at"
                  type="datetime-local"
                  required
                  defaultValue={localInput(new Date(Date.now() + 86400000))}
                />
              </label>
              <label>
                <span>Prioridade *</span>
                <select name="priority" defaultValue={opportunity.priority}>
                  {Object.entries(priorityLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                <span>Informação</span>
                <textarea name="description" rows={3} />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setAction(null)}>
                Cancelar
              </button>
              <button className="crm-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        {action === "vehicle" && (
          <form className="crm-subform" onSubmit={submitVehicle}>
            <h3>Viatura apresentada</h3>
            <div className="crm-form-grid">
              <label className="wide">
                <span>Viatura *</span>
                <select name="vehicle_id" required defaultValue="">
                  <option value="">Escolher</option>
                  {vehicles.map((v) => (
                    <option value={v.id} key={v.id}>
                      {v.registration || v.stock_number} · {v.make} {v.model}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Resposta do cliente *</span>
                <select name="response" defaultValue="offered">
                  {Object.entries(responseLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                <span>Notas</span>
                <textarea name="notes" rows={3} />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setAction(null)}>
                Cancelar
              </button>
              <button className="crm-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        {action === "transfer" && (
          <form className="crm-subform" onSubmit={submitTransfer}>
            <h3>Transferir oportunidade</h3>
            <div className="crm-form-grid">
              <label>
                <span>Novo responsável *</span>
                <select name="assigned_to" required defaultValue="">
                  {team
                    .filter((m) => m.user_id !== opportunity.assigned_to)
                    .map((m) => (
                      <option value={m.user_id} key={m.user_id}>
                        {m.display_name}
                      </option>
                    ))}
                </select>
              </label>
              <label className="wide">
                <span>Motivo *</span>
                <textarea name="reason" required rows={3} />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setAction(null)}>
                Cancelar
              </button>
              <button className="crm-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        {action === "lost" && (
          <form className="crm-subform" onSubmit={submitLost}>
            <h3>Encerrar como perdida</h3>
            <p className="subform-help">
              A oportunidade e todo o histórico permanecem pesquisáveis e podem
              ser reabertos se o contacto regressar.
            </p>
            <div className="crm-form-grid">
              <label>
                <span>Resultado *</span>
                <select name="outcome_code" required defaultValue="no_interest">
                  {Object.entries(outcomeLabels)
                    .filter(([v]) => v !== "sale")
                    .map(([v, l]) => (
                      <option value={v} key={v}>
                        {l}
                      </option>
                    ))}
                </select>
              </label>
              <label className="wide">
                <span>O que aconteceu? *</span>
                <textarea name="reason" required rows={4} />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setAction(null)}>
                Cancelar
              </button>
              <button className="danger solid" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        {action === "reopen" && (
          <form className="crm-subform" onSubmit={submitReopen}>
            <h3>Reabrir oportunidade</h3>
            <div className="crm-form-grid">
              <label className="wide">
                <span>Porque regressou ao acompanhamento? *</span>
                <textarea name="reason" required rows={3} />
              </label>
              <label className="wide">
                <span>Próxima tarefa *</span>
                <input name="task_title" required />
              </label>
              <label>
                <span>Data e hora *</span>
                <input
                  name="task_due_at"
                  type="datetime-local"
                  required
                  defaultValue={localInput(new Date(Date.now() + 86400000))}
                />
              </label>
              <label>
                <span>Prioridade</span>
                <select name="priority" defaultValue="normal">
                  {Object.entries(priorityLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setAction(null)}>
                Cancelar
              </button>
              <button className="crm-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        <section className="opportunity-vehicles">
          <h3>Viaturas apresentadas</h3>
          {suggestions.map((item) => {
            const vehicle = vehicleMap[item.vehicle_id];
            return (
              <article key={item.id}>
                <span>
                  <strong>
                    {vehicle?.make} {vehicle?.model}
                  </strong>
                  <small>
                    {vehicle?.registration || vehicle?.stock_number} ·{" "}
                    {item.relationship_type === "origin"
                      ? "Origem do contacto"
                      : responseLabels[item.response]}
                  </small>
                </span>
                {item.notes && <p>{item.notes}</p>}
              </article>
            );
          })}
        </section>
        <section className="opportunity-task-list">
          <h3>Tarefas da oportunidade</h3>
          {opportunityTasks.length === 0 ? (
            <p>Não existem tarefas.</p>
          ) : (
            opportunityTasks.map((task) => (
              <article key={task.id} className={`task-${task.status}`}>
                <div>
                  <strong>{task.title}</strong>
                  <small>
                    {formatDate(task.due_at)} · {priorityLabels[task.priority]}
                  </small>
                  {task.completion_note && (
                    <p>Resultado: {task.completion_note}</p>
                  )}
                  {task.withdrawal_reason && (
                    <p>Desistência: {task.withdrawal_reason}</p>
                  )}
                  {task.review_note && <p>Revisão: {task.review_note}</p>}
                </div>
                <span className={`crm-state ${task.status}`}>
                  {task.status === "open"
                    ? "Aberta"
                    : task.status === "completed"
                      ? "Concluída"
                      : task.status === "withdrawn"
                        ? task.review_status === "pending"
                          ? "Aguarda revisão"
                          : "Desistência revista"
                        : "Cancelada"}
                </span>
                {writable && task.status === "open" && (
                  <div className="task-actions">
                    <button
                      onClick={() => setTaskAction({ task, kind: "complete" })}
                    >
                      Concluir
                    </button>
                    <button
                      className="danger"
                      onClick={() => setTaskAction({ task, kind: "withdraw" })}
                    >
                      Desistir
                    </button>
                  </div>
                )}
                {writable &&
                  task.status === "withdrawn" &&
                  task.review_status === "pending" &&
                  (canSupervise ||
                    task.assigned_to === opportunity.assigned_to) && (
                    <button
                      onClick={() => setTaskAction({ task, kind: "review" })}
                    >
                      Rever
                    </button>
                  )}
              </article>
            ))
          )}
        </section>
        {taskAction?.kind === "complete" && (
          <form className="crm-subform" onSubmit={submitComplete}>
            <h3>Concluir tarefa e definir a próxima</h3>
            <div className="crm-form-grid">
              <label className="wide">
                <span>Resultado desta tarefa *</span>
                <textarea name="result" required rows={3} />
              </label>
              <label className="wide">
                <span>Próxima tarefa *</span>
                <input name="next_title" required />
              </label>
              <label>
                <span>Data e hora *</span>
                <input
                  name="next_due_at"
                  type="datetime-local"
                  required
                  defaultValue={localInput(new Date(Date.now() + 86400000))}
                />
              </label>
              <label>
                <span>Prioridade</span>
                <select
                  name="next_priority"
                  defaultValue={taskAction.task.priority}
                >
                  {Object.entries(priorityLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                <span>Informação</span>
                <textarea name="next_description" rows={2} />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setTaskAction(null)}>
                Cancelar
              </button>
              <button className="crm-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        {taskAction?.kind === "withdraw" && (
          <form className="crm-subform" onSubmit={submitWithdraw}>
            <h3>Desistir da tarefa</h3>
            <p className="subform-help">
              A tarefa não é apagada. Fica no histórico e aguarda revisão.
            </p>
            <div className="crm-form-grid">
              <label className="wide">
                <span>Justificação *</span>
                <textarea name="reason" required rows={5} />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setTaskAction(null)}>
                Cancelar
              </button>
              <button className="danger solid" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
        {taskAction?.kind === "review" && (
          <form className="crm-subform" onSubmit={submitReview}>
            <h3>Rever desistência</h3>
            <div className="crm-form-grid">
              <label>
                <span>Decisão *</span>
                <select name="decision" defaultValue="reactivate">
                  <option value="reactivate">Reativar tarefa</option>
                  <option value="accept">Aceitar desistência</option>
                </select>
              </label>
              <label className="wide">
                <span>Orientação ou fundamento *</span>
                <textarea name="note" required rows={3} />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setTaskAction(null)}>
                Cancelar
              </button>
              <button className="crm-primary" disabled={busy}>
                Guardar
              </button>
            </footer>
          </form>
        )}
      </section>
    </div>
  );
}
